/* eslint-disable no-unused-vars */
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Grid,
  GridItem,
  Icon,
  Input,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import moment from "moment";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useDropdownOptions } from "../../hooks/useDropdownOptions";
import apiClient from "../../utils/ApiClient/apiClientAxios";
import { toastService } from "../../utils/ApiClient/toastService";
import { Icons } from "../../WinterCountIconFIle";
import AddressFormSection from "./CaseView/create-case/AddressFormSection";

// Modern select styles override
const modernSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "36px",
    fontSize: "13px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#3B82F6" : "#E2E8F0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
    backgroundColor: "#FAFBFF",
    transition: "all 0.2s",
    "&:hover": { borderColor: "#94A3B8" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "13px",
    backgroundColor: state.isSelected
      ? "#3B82F6"
      : state.isFocused
        ? "#EFF6FF"
        : "white",
    color: state.isSelected ? "white" : "#1E293B",
    padding: "8px 12px",
  }),
  placeholder: (base) => ({ ...base, color: "#94A3B8", fontSize: "13px" }),
  singleValue: (base) => ({ ...base, fontSize: "13px", color: "#1E293B" }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#EFF6FF",
    borderRadius: "6px",
  }),
  multiValueLabel: (base) => ({ ...base, color: "#3B82F6", fontSize: "12px" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#93C5FD",
    "&:hover": { backgroundColor: "#DBEAFE", color: "#3B82F6" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "10px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
    border: "1px solid #E2E8F0",
    overflow: "hidden",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const SectionHeader = ({ icon, title, badge }) => (
  <Flex align="center" gap={2} mb={1}>
    <Flex
      align="center"
      justify="center"
      w="30px"
      h="30px"
      borderRadius="8px"
      bg="blue.50"
      color="blue.500"
      flexShrink={0}
    >
      <Icon as={icon} boxSize={4} />
    </Flex>
    <Text
      fontSize="13px"
      fontWeight="700"
      color="gray.700"
      letterSpacing="0.02em"
    >
      {title}
    </Text>
    {badge && (
      <Badge colorScheme="blue" fontSize="10px" borderRadius="full" px={2}>
        {badge}
      </Badge>
    )}
    <Divider flex={1} borderColor="gray.100" />
  </Flex>
);

const FieldLabel = ({ children, required }) => (
  <Text
    mb="6px"
    fontSize="12px"
    fontWeight="600"
    color="gray.600"
    letterSpacing="0.04em"
    textTransform="uppercase"
  >
    {children}
    {required && (
      <Text as="span" color="red.400" ml={1}>
        *
      </Text>
    )}
  </Text>
);

const modernInputStyle = {
  fontSize: "13px",
  height: "36px",
  borderRadius: "8px",
  borderColor: "#E2E8F0",
  bg: "#FAFBFF",
  _focus: {
    borderColor: "blue.400",
    boxShadow: "0 0 0 3px rgba(59,130,246,0.15)",
  },
  _hover: { borderColor: "gray.300" },
};

export default function CreateNewCaseForm({ onClose }) {
  const [loading, setLoading] = useState(false);
  const [isAddressVerified, setIsAddressVeirfied] = useState(false);

  const { options: raceOptions, isLoading: isRaceLoading } =
    useDropdownOptions("RACE");
  const {
    options: tribeOptions,
    isLoading: isJURISDICTIONAL_ASSIGNMENTLoading,
    raw: rawTribeData,
  } = useDropdownOptions("JURISDICTIONAL_ASSIGNMENT");
  const { options: caseTypeOptions } = useDropdownOptions("CASE_TYPE");
  const { options: genderOptions } = useDropdownOptions("GENDER");

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ADDRESS: [
        {
          TYPE: "HOME",
          PRIMARY: true,
          STREET_1: "",
          STREET_2: "",
          CITY: "",
          STATE: "",
          ZIP: "",
          COUNTY: "",
          COUNTRY: "US",
          TELEPHONE_HOME: "",
          TELEPHONE_MOBILE: "",
          TELEPHONE_WORK: "",
        },
      ],
      RACE: [],
      TRIBE: [],
      JURISDICTIONAL_ASSIGNMENT: [],
      PRODUCT: "",
      EVENT_DATE: "",
      BIRTH_DATE: "",
    },
  });

  const {
    fields: addressFields,
    prepend: prependAddress,
    remove: removeAddress,
  } = useFieldArray({ control, name: "ADDRESS" });
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    if (!data.PRODUCT || (!data.EVENT_DATE && !isAddressVerified)) {
      toastService.error(
        "Please fill Disease and Event Date also verify the address!",
      );
      return;
    }

    const addresses = data.ADDRESS.map((addr) => {
      const { TELEPHONE_WORK, TELEPHONE_WORK_EXT, ...rest } = addr;
      return {
        ...rest,
        ...(TELEPHONE_WORK && {
          TELEPHONE_WORK: TELEPHONE_WORK_EXT
            ? `${TELEPHONE_WORK} x ${TELEPHONE_WORK_EXT}`
            : TELEPHONE_WORK,
        }),
      };
    });

    const payload = {
      PERSON: {
        FIRST_NAME: data.FIRST_NAME,
        LAST_NAME: data.LAST_NAME,
        MIDDLE_NAME: data.MIDDLE_NAME,
        ALIAS: data.ALIAS,
        BIRTH_DATE: data.BIRTH_DATE
          ? moment(data.BIRTH_DATE).format("YYYYMMDD")
          : null,
        SSN: data.SSN,
        ADDRESS: addresses,
        RACE: data.RACE,
        TRIBE: data.TRIBE,
        GENDER: data.GENDER,
      },
      CASE: {
        PRODUCT: data.PRODUCT,
        EVENT_DATE: moment(data.EVENT_DATE).format("YYYYMMDD"),
        JURISDICTIONAL_ASSIGNMENT: data.JURISDICTIONAL_ASSIGNMENT.map(
          (val) => ({
            JURISDICTIONAL_ASSIGNMENT: val,
          }),
        ),
      },
    };

    setLoading(true);
    try {
      const response = await apiClient.post("/createCase", payload);
      const responseData = response.data;
      if (responseData?.id && responseData?.PERSON_ID) {
        toastService.success("Case Created Successfully!");
        navigate(`/CaseView/${responseData.id}/${responseData.PERSON_ID}`);
      } else {
        toastService.error(responseData?.error || "Failed to create case.");
        onClose();
      }
    } catch (err) {
      console.error("API error:", err);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="77vh" bg="gray.100" p={2}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid templateColumns="1.2fr 2fr">
          {/* LEFT PANEL — Case & Patient Info */}
          <GridItem p={1} borderRight="2px solid black">
            {/* Case Details */}
            <SectionHeader
              icon={Icons.DetailsIcon}
              title="Case Details"
              badge="Required"
            />

            <SimpleGrid columns={2} spacing={3} mb={6}>
              <Box>
                <FieldLabel required>Disease</FieldLabel>
                <Select
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={modernSelectStyles}
                  options={caseTypeOptions}
                  placeholder="Select Disease"
                  isSearchable
                  onChange={(selected) =>
                    setValue("PRODUCT", selected?.value, {
                      shouldValidate: true,
                    })
                  }
                  noOptionsMessage={() => "No match found"}
                />
                {errors.PRODUCT && (
                  <Text color="red.400" fontSize="11px" mt={1}>
                    Disease is required.
                  </Text>
                )}
              </Box>

              <Box>
                <FieldLabel required>Event Date</FieldLabel>
                <Input
                  type="date"
                  {...register("EVENT_DATE", { required: true })}
                  {...modernInputStyle}
                />
                {errors.EVENT_DATE && (
                  <Text color="red.400" fontSize="11px" mt={1}>
                    Event Date is required.
                  </Text>
                )}
              </Box>
            </SimpleGrid>

            {/* Patient Info */}
            <SectionHeader icon={Icons.UserIcon} title="Patient Information" />

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {[
                { label: "First Name", name: "FIRST_NAME" },
                { label: "Last Name", name: "LAST_NAME" },
                { label: "Middle Name", name: "MIDDLE_NAME" },
                { label: "Alias", name: "ALIAS" },
              ].map(({ label, name }) => (
                <Box key={name}>
                  <FieldLabel>{label}</FieldLabel>
                  <Input
                    size="sm"
                    {...register(name)}
                    {...modernInputStyle}
                    placeholder={`Enter ${label}`}
                  />
                </Box>
              ))}

              <FormControl>
                <FieldLabel>Tribe</FieldLabel>
                <Select
                  key={tribeOptions.length}
                  isMulti
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={modernSelectStyles}
                  options={tribeOptions}
                  value={tribeOptions.filter((opt) =>
                    watch("TRIBE")?.includes(opt.value),
                  )}
                  onChange={(selected) =>
                    setValue(
                      "TRIBE",
                      selected ? selected.map((o) => o.value) : [],
                    )
                  }
                  placeholder="Select Tribe"
                  isLoading={isJURISDICTIONAL_ASSIGNMENTLoading}
                  noOptionsMessage={() => "No match found"}
                />
              </FormControl>

              <FormControl>
                <FieldLabel>Race</FieldLabel>
                <Select
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={modernSelectStyles}
                  options={raceOptions}
                  isMulti
                  value={raceOptions.filter((opt) =>
                    watch("RACE")?.includes(opt.value),
                  )}
                  onChange={(selected) =>
                    setValue(
                      "RACE",
                      selected ? selected.map((opt) => opt.value) : [],
                    )
                  }
                  placeholder="Select Race"
                />
              </FormControl>

              <FormControl>
                <FieldLabel>Gender</FieldLabel>
                <Select
                  options={genderOptions}
                  isClearable
                  styles={modernSelectStyles}
                  value={
                    genderOptions.find(
                      (opt) => opt.value === watch("GENDER"),
                    ) || null
                  }
                  onChange={(selected) =>
                    setValue("GENDER", selected?.value || "")
                  }
                  placeholder="Select Gender"
                />
              </FormControl>

              <FormControl>
                <FieldLabel>Birth Date</FieldLabel>
                <Input
                  type="date"
                  {...register("BIRTH_DATE")}
                  {...modernInputStyle}
                />
              </FormControl>

              <FormControl>
                <FieldLabel>SSN</FieldLabel>
                <InputMask mask="999-99-9999" {...register("SSN")}>
                  {(inputProps) => (
                    <Input
                      {...inputProps}
                      {...modernInputStyle}
                      placeholder="000-00-0000"
                    />
                  )}
                </InputMask>
              </FormControl>

              <FormControl>
                <FieldLabel>Jurisdictional Assignment</FieldLabel>
                <Select
                  key={tribeOptions.length}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={modernSelectStyles}
                  options={tribeOptions}
                  isMulti
                  value={tribeOptions.filter((opt) =>
                    watch("JURISDICTIONAL_ASSIGNMENT")?.includes(opt.value),
                  )}
                  onChange={(selected) =>
                    setValue(
                      "JURISDICTIONAL_ASSIGNMENT",
                      selected ? selected.map((opt) => opt.value) : [],
                    )
                  }
                  isLoading={isJURISDICTIONAL_ASSIGNMENTLoading}
                  noOptionsMessage={() => "No match found"}
                  placeholder="Select assignment..."
                />
              </FormControl>
            </SimpleGrid>
          </GridItem>

          {/* RIGHT PANEL — Address */}
          <GridItem>
            <AddressFormSection
              addressFields={addressFields}
              prependAddress={prependAddress}
              removeAddress={removeAddress}
              register={register}
              setValue={setValue}
              watch={watch}
              setIsAddressVeirfied={setIsAddressVeirfied}
              control={control}
            />
          </GridItem>
        </Grid>

        {/* Footer */}
        <Flex
          justify="flex-end"
          gap={3}
          mt={1}
          pt={1}
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <Button
            size="sm"
            onClick={onClose}
            color="gray.500"
            fontWeight="600"
            fontSize="13px"
            borderRadius="8px"
            px={5}
            _hover={{ bg: "gray.100", color: "gray.700" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={loading}
            loadingText="Creating Case..."
            fontWeight="600"
            fontSize="13px"
            borderRadius="8px"
            px={6}
            bg="blue.500"
            color="white"
            boxShadow="0 2px 8px rgba(59,130,246,0.35)"
            _hover={{
              bg: "blue.600",
              boxShadow: "0 4px 12px rgba(59,130,246,0.4)",
            }}
            _active={{ bg: "blue.700" }}
            transition="all 0.2s"
          >
            Create Case
          </Button>
        </Flex>
      </form>
    </Box>
  );
}
